/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, mutatingPost, mutatingPut, type ApiResult } from "./client";
import type { ChangeEntry } from "./changes";
import type {
  AppointmentKind,
  AttentionReason,
  Concern,
  CostEstimate,
  DayBlock,
  DeliveryStatus,
  InsuranceDecision,
  MessageKind,
  MessageStatus,
  OnboardingStatus,
  OnboardingSummary,
  Weekday,
} from "./onboarding";
import { PAGE_SIZE, type Paged } from "./registry";
import { withSort, type SortChoice } from "../lib/sorting";

export type ReferralSource = "REFERRAL_FORM" | "SCREENER" | "FAX" | "CONSOLE";

export type ReferralInsurance = {
  payerName: string | null;
  memberId: string | null;
  groupNumber: string | null;
};

export const REFERRAL_STAGES = [
  "REFERRED",
  "PENDING_SCREENER",
  "SCREENER_SUBMITTED",
  "CONSULT_SCHEDULED",
  "INTAKE_SCHEDULED",
  "ACTIVE",
  "UNRESPONSIVE",
  "UNQUALIFIED",
  "CLOSED",
] as const;
export type ReferralStage = (typeof REFERRAL_STAGES)[number];

/** What has gone out to the family, by the sheet's Comm Status; a reminder carries its step's day. */
export type ReferralComms = "REACHED_OUT" | `REMINDER_${number}` | "SPOKEN_TO" | "COMPLETED";

export type ReferralListItem = {
  id: number;
  createdAt: string | null;
  status: string | null;
  stage: ReferralStage;
  comms: ReferralComms | null;
  lastTouchpointAt: string;
  /** The family's onboarding, once a referral invited one. */
  onboarding: OnboardingSummary | null;
  source: ReferralSource;
  organizationName: string | null;
  providerName: string | null;
  clientNames: string[];
  concerns: string | null;
  insurancePayerName: string | null;
};

export type NamedRef = { id: number; name: string | null };

export type ReferredClientView = {
  id: number;
  type: string | null;
  isCareRecipient: boolean;
  serviceLine: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  dob: string | null;
  stateCode: string | null;
  userId: number | null;
};

export type SourceFax = {
  caseId: string;
  ehrFaxId: string;
  receivedAt: string | null;
};

export type ReferralDetail = {
  id: number;
  status: string | null;
  onboardingId: string | null;
  source: ReferralSource;
  note: string | null;
  concerns: string | null;
  diagnosisCodes: string[];
  insurance: ReferralInsurance;
  createdAt: string | null;
  updatedAt: string | null;
  archivedAt: string | null;
  archivedReason: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  organization: NamedRef;
  provider: NamedRef | null;
  clients: ReferredClientView[];
  sourceFax: SourceFax | null;
};

/** A referred person as staff enter them; blanks are sent as null. */
export type IntakePerson = {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  dob: string | null;
  stateCode: string | null;
};

/** The canonical intake body: what a staff member creates a referral from, and edits it with. */
export type ReferralIntakeBody = {
  organizationId: number | null;
  providerId: number | null;
  child: IntakePerson | null;
  caregiver: IntakePerson | null;
  concerns: string | null;
  diagnosisCodes: string[];
  insurance: ReferralInsurance;
  note: string | null;
};

/** The canonical referral record nexus answers a write with; the detail page reloads after. */
export type ReferralRecord = { id: number; source: ReferralSource };

/** The days the list can say reminders went out, from the schedule an admin set. */
export function reminderDays(): Promise<ApiResult<{ days: number[] }>> {
  return api("/v1/referrals/reminder-days");
}

export function listReferrals(
  q: string,
  page: number,
  filter: string | null,
  sort: SortChoice,
): Promise<ApiResult<Paged<ReferralListItem>>> {
  const params = withSort(
    new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) }),
    sort,
  );
  if (q.trim() !== "") {
    params.set("q", q.trim());
  }
  if (filter) {
    params.set("filter", filter);
  }
  return api(`/v1/referrals?${params.toString()}`);
}


export const JOURNEY_NODES = [
  "REFERRED",
  "INVITED",
  "OPENED",
  "VERIFIED",
  "DETAILS",
  "CONCERNS",
  "INSURANCE",
  "CONFIRMATIONS",
  "ENROLLED",
  "SCHEDULE",
  "BOOKED",
  "INTAKE_REQUESTED",
  "WAITLIST",
  "FORMS",
  "ACTIVE",
  "CLOSED",
] as const;
export type JourneyNodeKey = (typeof JOURNEY_NODES)[number];

export type JourneyNodeState = "DONE" | "CURRENT" | "HELD" | "FAILED" | "TODO" | "SKIPPED";

export type JourneyPerson = {
  type: "CHILD" | "CAREGIVER" | null;
  careRecipient: boolean;
  firstName: string | null;
  lastName: string | null;
  userId: number | null;
};

export type JourneyCheck = {
  id: string;
  source: string | null;
  status: string | null;
  reason: string | null;
  generic: boolean;
  payerBasis: string | null;
  payerNameRaw: string | null;
  payerId: string | null;
  cost: CostEstimate | null;
  vendorStep: string | null;
  vendorRequestId: string | null;
  runState: string | null;
  attemptCount: number;
  askedAt: string | null;
  checkedAt: string | null;
  error: string | null;
};

export type JourneyAssignment = {
  kind: string;
  staffUserId: number | null;
  primary: boolean;
  since: string | null;
};

/** What each stage knows, keyed by the node that carries it; a stage with no more than its time is {}. */
export type JourneyFacts = {
  REFERRED: {
    source: ReferralSource;
    organizationId: number | null;
    providerId: number | null;
    createdByUserId: number | null;
    people: JourneyPerson[];
    concerns: string | null;
  };
  INVITED: { linkExpiresAt: string | null; resends: number };
  OPENED: Record<string, never>;
  VERIFIED: {
    dobAttempts: number;
    dobLockedAt: string | null;
    consentAt: string | null;
    consentVersion: string | null;
    zone: string | null;
    zoneChosenAt: string | null;
  };
  DETAILS: Record<string, never>;
  CONCERNS: { concerns: Concern[]; other: string | null; consultOnly: boolean };
  INSURANCE: { decision: InsuranceDecision | null; checks: JourneyCheck[] };
  CONFIRMATIONS: { confirmedAt: string | null; declinedAt: string | null };
  ENROLLED: {
    familyId: number | null;
    parentUserId: number | null;
    childUserId: number | null;
    provisioning: string | null;
    match: "NEW" | "RETURNING" | "ATTACHED" | null;
  };
  SCHEDULE: { kind: AppointmentKind | null; bookingState: string | null; familyChose: boolean };
  BOOKED: {
    appointmentId: string | null;
    healthieAppointmentId: string | null;
    startsAt: string | null;
    kind: AppointmentKind | null;
    providerId: string | null;
    providerFirstName: string | null;
    providerLastName: string | null;
  };
  INTAKE_REQUESTED: { weekdays: Weekday[]; blocks: DayBlock[] };
  WAITLIST: { detail: string | null };
  FORMS: Record<string, never>;
  ACTIVE: { team: JourneyAssignment[] };
  CLOSED: { reason: string | null };
};

export type JourneyNodeOf<K extends JourneyNodeKey> = {
  key: K;
  state: JourneyNodeState;
  at: string | null;
  facts: JourneyFacts[K];
};

/** A stage on the journey; switching on `key` narrows `facts` to that stage's shape. */
export type JourneyNode = { [K in JourneyNodeKey]: JourneyNodeOf<K> }[JourneyNodeKey];

export type JourneyEdge = { from: JourneyNodeKey; to: JourneyNodeKey; taken: boolean };

export type JourneyHold = {
  kind: OnboardingStatus;
  reason: AttentionReason | null;
  node: JourneyNodeKey | null;
  since: string;
  until: string | null;
  actorKind: string | null;
  actorUserId: number | null;
};

export type JourneyMessage = {
  id: string;
  kind: MessageKind;
  sequence: number | null;
  channel: "sms" | "email";
  status: MessageStatus;
  delivery: DeliveryStatus | null;
  skipReason: string | null;
  scheduledAt: string | null;
  deliveredAt: string | null;
  error: string | null;
};

export type JourneyFamily = {
  caregiverFirstName: string | null;
  caregiverLastName: string | null;
  caregiverPhone: string | null;
  caregiverEmail: string | null;
  childFirstName: string | null;
  childLastName: string | null;
  childDob: string | null;
  stateCode: string | null;
  zone: string | null;
};

/** The family's whole path from the referral to the first session, as nexus assembled it. */
export type ReferralJourney = {
  referralId: number;
  stage: ReferralStage;
  onboardingId: string | null;
  status: OnboardingStatus | null;
  family: JourneyFamily | null;
  nodes: JourneyNode[];
  edges: JourneyEdge[];
  holds: JourneyHold[];
  touches: JourneyMessage[];
  messages: JourneyMessage[];
  generatedAt: string;
};

export function referralJourney(id: string): Promise<ApiResult<ReferralJourney>> {
  return api(`/v1/referrals/${encodeURIComponent(id)}/journey`);
}

export function referralDetail(id: string): Promise<ApiResult<ReferralDetail>> {
  return api(`/v1/referrals/${encodeURIComponent(id)}`);
}

export function referralChanges(
  id: string,
  limit = 20,
): Promise<ApiResult<{ items: ChangeEntry[] }>> {
  return api(`/v1/referrals/${encodeURIComponent(id)}/changes?limit=${limit}`);
}

export function createReferral(
  body: ReferralIntakeBody,
  idempotencyKey: string,
): Promise<ApiResult<ReferralRecord>> {
  return api("/v1/referrals/", mutatingPost(body, idempotencyKey));
}

export function updateReferral(
  id: number,
  body: ReferralIntakeBody,
  idempotencyKey: string,
): Promise<ApiResult<ReferralRecord>> {
  return api(`/v1/referrals/${id}`, mutatingPut(body, idempotencyKey));
}
